import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Task } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<TasksGetParams>
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Task>>,
    'queryKey' | 'queryFn'
  >
}

interface TasksGetParams {
  limit?: number
  offset?: number
  sort?: string
  search?: string
  hideArchivedTasks?: boolean
  status?: string
  contractorId?: number
  projectId?: number
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  hideArchivedTasks: 'boolean',
  status: 'string',
  contractorId: 'number',
  projectId: 'number',
}

export const useTaskListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)
  const queryParams = buildRequestParams<TasksGetParams>(
    transformedParams,
    schema,
  )
  return useQuery<unknown, Error, APIList<Task>>(
    [Entities.Tasks, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        search,
        hideArchivedTasks,
        status,
        contractorId,
        projectId,
      } = queryParams
      const { data } = await api.v1AdminTasksGet(
        limit,
        offset,
        sort,
        search,
        typeof hideArchivedTasks === 'boolean' ? hideArchivedTasks : undefined,
        status,
        contractorId,
        projectId,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
